"use client"

import clsx from "clsx"
import Image from "next/image"
import React, { useEffect, useRef, useState } from "react"
import { TLoader } from "../display/TLoader"

/**
 * @description 미디어 이미지 표시. 투명 배경 체크 패턴, 로딩 상태 지원
 * @example
 * ```tsx
 * <TMediaImage src="/image.png" aspectRatio="16/9" isBgCheckPattern />
 * ```
 */
interface TMediaImageProps {
	/** 이미지 URL */
	src?: string
	/** 너비. 숫자(px) 또는 문자열("100%") */
	width?: number | string
	/** 높이. 숫자(px) 또는 문자열("100%") */
	height?: number | string
	/** CSS aspect-ratio 값 (예: "16/9", "1/1") */
	aspectRatio?: string
	/** CORS 설정 */
	crossOrigin?: "anonymous" | "use-credentials"
	/** 외부에서 로딩 완료 상태를 제어 */
	isLoaded?: boolean
	/** 투명 배경 체크 패턴 표시 (PNG 등에 사용) */
	isBgCheckPattern?: boolean
	/** 추가 CSS 클래스 */
	className?: string
	/** 이미지 대체 텍스트 */
	alt?: string
	/** Next.js Image priority 로딩 */
	priority?: boolean
	/**
	 * 이미지 로딩 전략
	 * @default "lazy"
	 */
	loading?: "eager" | "lazy"
	/** 이미지 로드 실패 시 콜백 */
	onError?: () => void
}

export const TMediaImage = React.memo((props: TMediaImageProps) => {
	const {
		src,
		width = "100%",
		height = "100%",
		aspectRatio = "1/1",
		crossOrigin,
		isLoaded,
		isBgCheckPattern,
		className,
		alt,
		priority = false,
		loading = "lazy",
		onError
	} = props

	const [loadingState, setLoadingState] = useState<boolean>(true)
	const imgRef = useRef<HTMLImageElement | null>(null)

	useEffect(() => {
		setLoadingState(!isLoaded)
	}, [isLoaded])

	const handleImageLoad = React.useCallback(() => {
		setLoadingState(false)
	}, [])

	const handleImageError = React.useCallback(() => {
		setLoadingState(false)
		onError?.()
	}, [onError])

	useEffect(() => {
		if (imgRef.current && imgRef.current.complete) {
			if (imgRef.current.naturalWidth > 0) {
				handleImageLoad()
			}
		}
	}, [])

	return (
		<section className={clsx("TMedia image", className)} style={{ width, height, aspectRatio }}>
			{loadingState && <TLoader size={24} className="loader" />}
			{src ? (
				<Image
					ref={imgRef}
					src={src || ""}
					fill
					crossOrigin={crossOrigin}
					alt={alt ?? "Media image"}
					onLoad={handleImageLoad}
					onError={handleImageError}
					className={clsx({ invisible: loadingState })}
					loading={priority ? "eager" : loading}
					priority={priority}
					unoptimized
					style={{ objectFit: "contain" }}
				/>
			) : null}
			{isBgCheckPattern && <div className="media-bg-pattern" />}
		</section>
	)
})

TMediaImage.displayName = "TMediaImage"
